import { useState } from 'react';
import moment from 'moment';
import { Button, Table, Icon } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import React from 'react';
import { Customer } from '../../../../../core-module/services/logistics/types';
import { OrderHistoryItemComponent } from './OrderHistoryItemComponent';
import { Delivery } from '../../../../../core-module/models/OrderHistory/OrderHistory';
import Swal from 'sweetalert2';
import { delivererClient } from '../../../../../core-module/services/deliverer/DelivererClient';

interface Props {
  readonly deliveries: Delivery[];
  readonly customer: Customer;
  readonly metadata?: {
    pageCount?: number;
    totalItemCount?: number;
    pageNumber?: number;
    hasPreviousPage?: boolean;
    hasNextPage?: boolean;
  };
  readonly pageNumber?: number;
  readonly onNextPage?: () => void;
  readonly onPrevPage?: () => void;
  readonly onEditClick: (delivery: Delivery) => void;
}

const OrderHistoryComponent: React.FC<Props> = ({
  deliveries,
  customer,
  metadata,
  pageNumber,
  onNextPage,
  onPrevPage,
  onEditClick
}) => {
  const [deletedDeliveryIds, setDeletedDeliveryIds] = useState<Set<string>>(new Set());

  const handleDelete = async (rowData) => {
    var result = await Swal({
      title: 'Let op',
      html: `Wilt u de bestelling voor ${moment(rowData.requestedDeliveryMoment).format(
        'dddd DD MMMM, HH:mm'
      )} verwijderen`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja',
      cancelButtonText: 'Nee',
      showLoaderOnConfirm: true,
      allowOutsideClick: false,

      preConfirm: async () => {
        return delivererClient
          .cancelSalesOrderDelivery(rowData.salesOrderId, rowData.deliveryId)
          .catch((error) => {
            Swal.showValidationMessage('Verwijderen mislukt');
          });
      }
    });

    if (result.value) {
      setDeletedDeliveryIds((prev) =>
        new Set(prev).add(`${rowData.salesOrderId}-${rowData.deliveryId}`)
      );
    }
  };

  const visibleDeliveries = deliveries.filter(
    (delivery) => !deletedDeliveryIds.has(`${delivery.salesOrderId}-${delivery.deliveryId}`)
  );

  const columns: ColumnProps<Delivery>[] = [
    {
      title: 'Bezorgmoment',
      dataIndex: '',
      key: 'bezorgmoment',
      render: (_, delivery) =>
        moment(delivery.requestedDeliveryMoment).format('dddd DD MMMM, HH:mm')
    },
    {
      title: 'Maaltijden',
      dataIndex: '',
      key: 'dishes',
      render: (_, delivery) => (
        <OrderHistoryItemComponent item={delivery.items} delivery={delivery} customer={customer} />
      )
    },
    {
      title: 'Acties',
      dataIndex: '',
      key: 'actions',
      width: '100px',
      render: (_, rowData) => {
        const canEdit = rowData.actions.some(
          (action) => action.name === 'change-delivery-information'
        );

        const canCancel = rowData.actions.some(
          (action) => action.name === 'cancel-sales-order-delivery'
        );

        return (
          <>
            {canEdit && (
              <Button
                style={{ marginRight: 5, marginBottom: 5, top: '-0px' }}
                onClick={() => onEditClick(rowData)}
                type='primary'
                htmlType='button'
              >
                <Icon type='edit' />
              </Button>
            )}

            {canCancel && (
              <Button
                style={{ marginRight: 5, marginBottom: 5, top: '-0px' }}
                onClick={() => handleDelete(rowData)}
                type='danger'
                htmlType='button'
              >
                <Icon type='delete' />
              </Button>
            )}
          </>
        );
      }
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Table
        rowKey={(delivery) => `${delivery.salesOrderId}-${delivery.deliveryId}`}
        rowClassName={() => 'striped-table-row'}
        bordered
        size='small'
        columns={columns}
        dataSource={visibleDeliveries}
        pagination={false}
        locale={{ emptyText: 'Geen data beschikbaar' }}
        style={{ marginBottom: 16 }}
      />
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          marginBottom: 12,
          justifyContent: 'center'
        }}
      >
        <Button onClick={onPrevPage} disabled={!metadata?.hasPreviousPage}>
          Vorige
        </Button>
        <span>
          Pagina {metadata?.pageNumber ?? pageNumber ?? 1} van {metadata?.pageCount ?? '1'}
        </span>
        <Button onClick={onNextPage} disabled={!metadata?.hasNextPage}>
          Volgende
        </Button>
      </div>
    </div>
  );
};

export { OrderHistoryComponent };
